import { CircularProgress } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Jaktdag, JaktdagResponse } from "~/src/api/types";
import { StorageContext, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";
import { useClientState } from "~/src/hooks/useClientState";
import { ArtType } from "../../../helpers/types";
import { getJaktdagQuery } from "../../../react-query/queries";
import TomtResultat from "../../Oversikt/Components/TomtResultat";
import RegistrerElg from "./RegistrerElg/RegistrerElg";
import RegistrerHjort from "./RegistrerHjort/RegistrerHjort";

const EndreJaktdag = () => {
  const { jaktdagId } = useParams();
  const storage = useContext(StorageContext);
  const queryClient = useQueryClient();
  const queryKey = ["jaktdagId-" + jaktdagId, { jaktdagId: jaktdagId }];
  const [jaktdag, setJaktdag] = useState<JaktdagResponse | null | undefined>(
    storage.setIndividerForJaktdag(queryClient.getQueryData(queryKey)),
  );
  const clientState = useClientState();
  const isOnline = clientState === "Online";
  const jaktdagQuery = useQuery({
    queryKey: queryKey,
    queryFn: getJaktdagQuery,
    enabled:
      jaktdagId !== undefined &&
      storage.get<JaktdagResponse>({ id: jaktdagId }, "Jaktdag") === undefined,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    const storedJaktdag = storage.get<Jaktdag>({ id: jaktdagId }, "Jaktdag");
    let _jaktdag: Jaktdag | undefined = undefined;
    let _storedJaktdag: StorageItem<Jaktdag> | undefined = undefined;

    if (
      storedJaktdag !== undefined &&
      ["update", "create"].includes(storedJaktdag.operation || "other")
    ) {
      _storedJaktdag = storedJaktdag;
    } else if (
      isOnline &&
      jaktdagQuery.data !== undefined &&
      typeof jaktdagQuery.data !== "string" &&
      "Jaktfelt" in jaktdagQuery.data
    ) {
      _jaktdag = jaktdagQuery.data as Jaktdag;
    }

    const jaktdagToSet = _storedJaktdag?.data || _jaktdag;
    if (_storedJaktdag && _jaktdag) {
      _storedJaktdag.data.Individer = _jaktdag.Individer;
    }
    if (jaktdagToSet) {
      setJaktdag(storage.setIndividerForJaktdag(jaktdagToSet));
    }
  }, [isOnline, jaktdagQuery.data]);

  if ((jaktdagQuery.isLoading || jaktdagQuery.isFetching) && !jaktdag) {
    return (
      <TomtResultat>
        <CircularProgress thickness={7} size={80} />
      </TomtResultat>
    );
  }

  if (!jaktdag) {
    return <TomtResultat>{t("errors.notFound", { type: "Jaktdag" })}</TomtResultat>;
  }

  return jaktdag?.Art === ArtType.Elg ? (
    <RegistrerElg
      initialData={{ ...jaktdag, Id: jaktdagId ?? jaktdag.Id }}
      key={jaktdag.Individer?.length}
    />
  ) : jaktdag?.Art === ArtType.Hjort ? (
    <RegistrerHjort
      initialData={{ ...jaktdag, Id: jaktdagId ?? jaktdag.Id }}
      key={jaktdag.Individer?.length}
    />
  ) : (
    <></>
  );
};

export default EndreJaktdag;
